import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useQueryClient} from 'react-query';
import {Container} from 'react-bootstrap';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {DateTime} from 'luxon';
import {PageTitle} from '../../../_metronic/layout/core';
import toast from 'react-hot-toast';
import {
  AdresseSuggestion,
  ClientFormValues,
  CommandePayload,
  EligibilityFormValues,
  EligibilityResult,
  GpsCoordinates,
  OrderFormValues,
  SelectOption,
} from '../Types/types';
import {
  DIVOPS_HISTORY_QUERY_KEY,
  useCheckEligibility,
  useCreateCommande,
  useGeocodeAdresse,
  useGetContacts,
  useGetPortesCollecte,
  useGetSearchHistory,
  useReverseGeocodeAdresse,
} from '../Hooks/DivopsHooks';
import AdresseAutocomplete from '../Components/AdresseAutocomplete';
import AdresseMap from '../Components/AdresseMap';
import SiretAdresseSelect from '../Components/SiretAdresseSelect';
import DivopsAssistanceModal from '../Components/DivopsAssistanceModal';
import Level3Title from '../../../app/common/components/title/Level3Title';

const REFERENCE_MAX_LENGTH = 20;

const DUREES_ENGAGEMENT: SelectOption[] = [
  {value: '', label: "Sélectionnez une durée d'engagement"},
  {value: '12', label: '12 mois'},
  {value: '24', label: '24 mois'},
  {value: '36', label: '36 mois'},
];

const DEFAULT_ELIGIBILITY_FORM: EligibilityFormValues = {
  adresse: '',
  codePostal: '',
  codeInsee: '',
};

const DEFAULT_ORDER_FORM: OrderFormValues = {
  produitId: '',
  typeLienDivop: '',
  dureeEngagement: '',
  gtr247: false,
  referenceCommandeInterne: '',
  referenceFacturationInterne: '',
  commentaire: '',
  portLivraisonPrincipal: '',
  vlanLivraison: '',
  optionSecurisation: false,
  portLivraisonSecondaire: '',
  vlanLivraisonSecondaire: '',
};

const DEFAULT_CLIENT_FORM: ClientFormValues = {
  contactExistant: false,
  contactId: '',
  emailContact: '',
  nom: '',
  prenom: '',
  telephoneFixe: '',
  telephoneMobile: '',
  conformiteRgpd: false,
  siret: '',
  societe: '',
  adresse: '',
  details: '',
  codePostal: '',
  ville: '',
  porte: '',
  escalier: '',
  batiment: '',
  ensemble: '',
  pto: '',
};

const DivopsPage = (): JSX.Element => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [eligibilityForm, setEligibilityForm] =
    useState<EligibilityFormValues>(DEFAULT_ELIGIBILITY_FORM);
  const [eligibilityResult, setEligibilityResult] = useState<EligibilityResult | null>(null);
  const [orderForm, setOrderForm] = useState<OrderFormValues>(DEFAULT_ORDER_FORM);
  const [clientForm, setClientForm] = useState<ClientFormValues>(DEFAULT_CLIENT_FORM);
  const [autocompleteResetKey, setAutocompleteResetKey] = useState(0);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [showAssistanceModal, setShowAssistanceModal] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasGps = eligibilityForm.latitude !== undefined && eligibilityForm.longitude !== undefined;
  const geocodeQuery = hasGps
    ? ''
    : [eligibilityForm.adresse, eligibilityForm.codePostal].filter(Boolean).join(' ');
  const {data: geocodedPosition, isSuccess: isGeocoded} = useGeocodeAdresse(geocodeQuery);
  const mapPosition: GpsCoordinates | null = hasGps
    ? {latitude: eligibilityForm.latitude as number, longitude: eligibilityForm.longitude as number}
    : geocodedPosition ?? null;

  const {mutate: reverseGeocode} = useReverseGeocodeAdresse();

  const handleMapPositionChange = useCallback(
    (coords: GpsCoordinates) => {
      setEligibilityForm({
        adresse: '',
        codePostal: '',
        codeInsee: '',
        latitude: coords.latitude,
        longitude: coords.longitude,
      });
      const showInInput = (label: string) => {
        setAddressInputValue(label);
        setAutocompleteResetKey((key) => key + 1);
      };
      const coordsLabel = `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`;
      reverseGeocode(coords, {
        onSuccess: (label) => showInInput(label ?? coordsLabel),
        onError: () => showInInput(coordsLabel),
      });
    },
    [reverseGeocode],
  );

  const {data: contacts = [], isLoading: isLoadingContacts} = useGetContacts();
  const {data: portesCollecte = [], isLoading: isLoadingPortesCollecte} = useGetPortesCollecte();
  const {data: searchHistoryResponse} = useGetSearchHistory({page: 1, perPage: 5});
  const recentSearches = searchHistoryResponse?.data ?? [];
  const checkEligibilityMutation = useCheckEligibility();
  const createCommandeMutation = useCreateCommande();

  const getStepClass = (step: number): string => {
    if (step < currentStep) return 'stepper-item completed';
    if (step === currentStep) return 'stepper-item current';
    return 'stepper-item';
  };

  const runEligibilityCheck = (form: EligibilityFormValues) => {
    setErrorMessage(null);
    const hasAddress = !!(form.adresse && form.codePostal && form.codeInsee);
    const hasGps = form.latitude !== undefined && form.longitude !== undefined;
    if (!hasAddress && !hasGps) {
      setErrorMessage(intl.formatMessage({id: 'DIVOPS.ELIGIBILITY.ERROR.REQUIRED'}));
      return;
    }
    checkEligibilityMutation.mutate(form, {
      onSuccess: (result) => {
        setEligibilityResult(result);
        setCurrentStep(2);
        queryClient.invalidateQueries(DIVOPS_HISTORY_QUERY_KEY);
      },
      onError: () => {
        setErrorMessage(intl.formatMessage({id: 'DIVOPS.ELIGIBILITY.ERROR.SERVER'}));
      },
    });
  };

  const handleSearch = () => runEligibilityCheck(eligibilityForm);

  const prefillAddress = (form: EligibilityFormValues, label?: string) => {
    setEligibilityForm(form);
    setAddressInputValue(label ?? [form.adresse, form.codePostal].filter(Boolean).join(' '));
    setAutocompleteResetKey((key) => key + 1);
  };

  const handleRelaunchSearch = (form: EligibilityFormValues, label?: string) => {
    document.body.scrollTo({top: 0, behavior: 'smooth'});
    prefillAddress(form, label);
    runEligibilityCheck(form);
  };

  useEffect(() => {
    const replay = (
      location.state as {replaySearch?: EligibilityFormValues & {label?: string}} | null
    )?.replaySearch;
    if (replay) {
      const {label, ...form} = replay;
      prefillAddress(form, label);
      runEligibilityCheck(form);
      navigate(location.pathname, {replace: true, state: {}});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectLien = (type: 'ftto' | 'ftte') => {
    if (!eligibilityResult) return;
    const produits =
      type === 'ftto' ? eligibilityResult.ftto.produits : eligibilityResult.ftte?.produits ?? [];
    setOrderForm({
      ...DEFAULT_ORDER_FORM,
      typeLienDivop: type,
      produitId: produits[0]?.id ?? '',
    });
    setClientForm((prev) => ({
      ...prev,
      codePostal: eligibilityForm.codePostal,
    }));
    setSubmitAttempted(false);
    setCurrentStep(3);
  };

  const getOrderErrors = (): Record<string, boolean> => {
    const checks: [string, boolean][] = [
      ['produitId', !orderForm.produitId],
      ['dureeEngagement', !orderForm.dureeEngagement],
      ['portLivraisonPrincipal', !orderForm.portLivraisonPrincipal],
      ['vlanLivraison', !orderForm.vlanLivraison],
      ['contactId', clientForm.contactExistant && !clientForm.contactId],
      ['emailContact', !clientForm.contactExistant && !clientForm.emailContact],
      ['nom', !clientForm.nom],
      ['prenom', !clientForm.prenom],
      ['telephoneFixe', !clientForm.telephoneFixe],
      ['telephoneMobile', !clientForm.telephoneMobile],
      ['siret', !clientForm.siret],
      ['societe', !clientForm.societe],
      ['adresse', !clientForm.adresse],
      ['codePostal', !clientForm.codePostal],
      ['ville', !clientForm.ville],
      ['conformiteRgpd', !clientForm.conformiteRgpd],
    ];
    return Object.fromEntries(checks.filter(([, invalid]) => invalid));
  };

  const errors: Record<string, boolean> = submitAttempted ? getOrderErrors() : {};
  const invalidClass = (key: string): string => (errors[key] ? ' is-invalid' : '');

  const handleSubmitOrder = () => {
    setSubmitAttempted(true);
    setErrorMessage(null);
    const errorKeys = Object.keys(getOrderErrors());
    if (errorKeys.length > 0) {
      const orderKeys = ['produitId', 'dureeEngagement', 'portLivraisonPrincipal', 'vlanLivraison'];
      const messageId =
        errorKeys.length === 1 && errorKeys[0] === 'conformiteRgpd'
          ? 'DIVOPS.CLIENT.ERROR.RGPD'
          : errorKeys.some((key) => orderKeys.includes(key))
          ? 'DIVOPS.ORDER.ERROR.REQUIRED'
          : 'DIVOPS.CLIENT.ERROR.REQUIRED';
      setErrorMessage(intl.formatMessage({id: messageId}));
      const firstField = document.getElementById(`order-${errorKeys[0]}`);
      firstField?.scrollIntoView({block: 'center', behavior: 'smooth'});
      firstField?.focus({preventScroll: true});
      return;
    }
    const emailForContact = clientForm.contactExistant
      ? clientForm.contactId
      : clientForm.emailContact;

    const payload: CommandePayload = {
      lienId: orderForm.produitId,
      produitLabel: activeProduits.find((p) => p.id === orderForm.produitId)?.label ?? '',
      typeLien: orderForm.typeLienDivop as 'ftto' | 'ftte',
      dureeEngagement: orderForm.dureeEngagement,
      gtrOption: orderForm.gtr247,
      refCommandeInterne: orderForm.referenceCommandeInterne,
      refFactureInterne: orderForm.referenceFacturationInterne,
      commentaire: orderForm.commentaire,
      portLivraisonPrincipal: orderForm.portLivraisonPrincipal,
      vlanLivraison: orderForm.vlanLivraison,
      ...(orderForm.optionSecurisation && orderForm.portLivraisonSecondaire
        ? {
            portLivraisonSecondaire: orderForm.portLivraisonSecondaire,
            vlanLivraisonSecondaire: orderForm.vlanLivraisonSecondaire,
          }
        : {}),
      contactExistant: clientForm.contactExistant,
      emailContact: emailForContact,
      nomContact: clientForm.nom,
      prenomContact: clientForm.prenom,
      telephoneFixe: clientForm.telephoneFixe,
      telephoneMobile: clientForm.telephoneMobile,
      siret: clientForm.siret,
      societe: clientForm.societe,
      adresse: clientForm.adresse,
      details: clientForm.details || undefined,
      codePostal: clientForm.codePostal,
      ville: clientForm.ville,
      codeInsee: eligibilityResult!.codeInsee,
      ...(orderForm.typeLienDivop === 'ftte'
        ? {
            porte: clientForm.porte || undefined,
            escalier: clientForm.escalier || undefined,
            batiment: clientForm.batiment || undefined,
            ensemble: clientForm.ensemble || undefined,
            pto: clientForm.pto || undefined,
          }
        : {}),
    };

    createCommandeMutation.mutate(payload, {
      onSuccess: () => {
        toast.success(intl.formatMessage({id: 'DIVOPS.ORDER.SUCCESS'}));
        setErrorMessage(null);
        setSubmitAttempted(false);
        setCurrentStep(1);
        setEligibilityForm(DEFAULT_ELIGIBILITY_FORM);
        setEligibilityResult(null);
        setOrderForm(DEFAULT_ORDER_FORM);
        setClientForm(DEFAULT_CLIENT_FORM);
        setAddressInputValue('');
        setAutocompleteResetKey((key) => key + 1);
      },
      onError: (error: any) => {
        const serverMessage = error?.response?.data?.message;
        setErrorMessage(serverMessage ?? intl.formatMessage({id: 'DIVOPS.ORDER.ERROR.SERVER'}));
      },
    });
  };

  const activeProduits =
    orderForm.typeLienDivop === 'ftto'
      ? eligibilityResult?.ftto.produits ?? []
      : eligibilityResult?.ftte?.produits ?? [];

  const hasResolvedAddress = !!eligibilityResult?.codeInsee;
  const canCommandFtto = hasResolvedAddress && eligibilityResult?.ftto.verif === 1;
  const canCommandFtte = hasResolvedAddress && eligibilityResult?.ftte?.eligible === true;

  return (
    <Container fluid className="px-0">
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'DIVOPS.PAGE.TITLE'})}</PageTitle>

      <div
        className="mx-2 mx-md-5 rounded d-flex flex-column align-items-center justify-content-center p-4 p-md-8 p-lg-12"
        style={{backgroundColor: '#0e5183'}}
      >
        <div className="d-flex flex-column align-items-center justify-content-center text-white text-center">
          <span
            className="fw-bold py-2 py-md-3 px-3 px-md-5"
            style={{fontSize: 'clamp(1.25rem, 4vw, 2rem)', fontWeight: 700}}
          >
            {intl.formatMessage({id: 'DIVOPS.ELIGIBILITY.TITLE'})}
          </span>
        </div>

        <div
          className="card w-100 position-relative"
          style={{maxWidth: '1260px', borderRadius: '1rem'}}
        >
          <button
            type="button"
            className="btn btn-icon btn-sm btn-light-primary position-absolute top-0 end-0 m-4"
            title={intl.formatMessage({id: 'DIVOPS.ASSISTANCE.HELP_BUTTON'})}
            onClick={() => setShowAssistanceModal(true)}
          >
            <i className="fas fa-question-circle" style={{fontSize: '1.4rem'}}></i>
          </button>
          {errorMessage && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center p-4"
              style={{zIndex: 10, backgroundColor: 'rgba(255, 255, 255, 0.92)', borderRadius: '1rem'}}
              role="alert"
            >
              <div
                className="alert alert-danger d-flex align-items-center shadow mb-0"
                style={{maxWidth: 640}}
              >
                <i className="fas fa-exclamation-circle fs-3 me-3"></i>
                <span className="flex-grow-1">{errorMessage}</span>
                <button
                  type="button"
                  className="btn-close ms-3"
                  onClick={() => setErrorMessage(null)}
                ></button>
              </div>
            </div>
          )}
          <div className="card-body py-6 py-md-10">
            <div className="stepper stepper-pills d-flex flex-column">
              <div className="text-center mb-10"></div>
              {/* begin::Nav */}
              <div className="stepper-nav d-flex flex-column flex-md-row justify-content-start justify-content-md-center align-items-start align-items-md-center mb-10 gap-3 gap-md-0">
                <div className={getStepClass(1)}>
                  <div className="stepper-wrapper d-flex align-items-center">
                    <div
                      className="stepper-icon w-40px h-40px me-1"
                      style={{border: '1px solid #1071b929'}}
                    >
                      <i className="stepper-check fas fa-check"></i>
                      <span className="stepper-number">1</span>
                    </div>
                    <div className="stepper-label ms-3">
                      <h3 className="stepper-title">
                        {intl.formatMessage({id: 'DIVOPS.STEP1.TITLE'})}
                      </h3>
                      <div className="stepper-desc d-none d-md-block">
                        {intl.formatMessage({id: 'DIVOPS.STEP1.DESC'})}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="stepper-line mx-3 mx-md-6 d-none d-md-block"
                  style={{height: '2px'}}
                ></div>

                <div className={getStepClass(2)}>
                  <div className="stepper-wrapper d-flex align-items-center">
                    <div
                      className="stepper-icon w-40px h-40px me-1"
                      style={{border: '1px solid #1071b929'}}
                    >
                      <i className="stepper-check fas fa-check"></i>
                      <span className="stepper-number">2</span>
                    </div>
                    <div className="stepper-label ms-3">
                      <h3 className="stepper-title">
                        {intl.formatMessage({id: 'DIVOPS.STEP2.TITLE'})}
                      </h3>
                      <div className="stepper-desc d-none d-md-block">
                        {intl.formatMessage({id: 'DIVOPS.STEP2.DESC'})}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="stepper-line mx-3 mx-md-6 d-none d-md-block"
                  style={{height: '2px'}}
                ></div>

                <div className={getStepClass(3)}>
                  <div className="stepper-wrapper d-flex align-items-center">
                    <div
                      className="stepper-icon w-40px h-40px me-1"
                      style={{border: '1px solid #1071b929'}}
                    >
                      <i className="stepper-check fas fa-check"></i>
                      <span className="stepper-number">3</span>
                    </div>
                    <div className="stepper-label ms-3">
                      <h3 className="stepper-title">
                        {intl.formatMessage({id: 'DIVOPS.STEP3.TITLE'})}
                      </h3>
                      <div className="stepper-desc d-none d-md-block">
                        {intl.formatMessage({id: 'DIVOPS.STEP3.DESC'})}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* end::Nav */}

              {/* begin::Content */}
              <div className="px-lg-5">
                {/* Step 1 — Test d'éligibilité */}
                {currentStep === 1 && (
                  <div>
                    <div className="fv-row mb-10">
                      <label className="d-flex align-items-center fs-5 fw-semibold mb-2">
                        <span className="required">
                          {intl.formatMessage({id: 'DIVOPS.ELIGIBILITY.ADRESSE'})}
                        </span>
                      </label>
                      <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-start gap-3">
                        <div className="flex-grow-1">
                          <AdresseAutocomplete
                            key={autocompleteResetKey}
                            initialValue={addressInputValue}
                            placeholder={intl.formatMessage({
                              id: 'DIVOPS.ELIGIBILITY.ADRESSE.PLACEHOLDER',
                            })}
                            onSelect={(suggestion: AdresseSuggestion) =>
                              setEligibilityForm({
                                adresse: [suggestion.numero, suggestion.rue]
                                  .filter(Boolean)
                                  .join(' '),
                                codePostal: suggestion.codePostal,
                                codeInsee: suggestion.codeInsee,
                                latitude: undefined,
                                longitude: undefined,
                              })
                            }
                            onSelectGps={(coords) =>
                              setEligibilityForm({
                                adresse: '',
                                codePostal: '',
                                codeInsee: '',
                                latitude: coords.latitude,
                                longitude: coords.longitude,
                              })
                            }
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-lg btn-primary flex-shrink-0 w-100 w-sm-auto"
                          onClick={handleSearch}
                          disabled={checkEligibilityMutation.isLoading}
                        >
                          {checkEligibilityMutation.isLoading ? (
                            <span className="indicator-progress d-flex align-items-center">
                              {intl.formatMessage({id: 'WAIT'})}
                              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                          ) : (
                            <>
                              <i className="fas fa-search me-2 fs-5"></i>
                              {intl.formatMessage({id: 'DIVOPS.ELIGIBILITY.SEARCH'})}
                            </>
                          )}
                        </button>
                      </div>
                      <div className="mt-4">
                        <AdresseMap
                          position={mapPosition}
                          onPositionChange={handleMapPositionChange}
                        />
                        <div className="form-text">
                          {intl.formatMessage({
                            id:
                              isGeocoded && !geocodedPosition && !hasGps
                                ? 'DIVOPS.ELIGIBILITY.MAP.GEOCODE_ERROR'
                                : 'DIVOPS.ELIGIBILITY.MAP.HINT',
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2 — Résultats */}
                {currentStep === 2 && eligibilityResult && (
                  <div>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-8">
                      <h2 className="fw-bold mb-0">
                        {intl.formatMessage({id: 'DIVOPS.RESULTS.TITLE'})}
                      </h2>
                      <button
                        type="button"
                        className="btn btn-light-primary"
                        onClick={() => setCurrentStep(1)}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        {intl.formatMessage({id: 'DIVOPS.RESULTS.BACK'})}
                      </button>
                    </div>

                    {!eligibilityResult.codeInsee ? (
                      <div className="alert alert-danger d-flex align-items-center">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {intl.formatMessage({id: 'DIVOPS.RESULTS.ERROR.NO_ADDRESS'})}
                      </div>
                    ) : (
                      <>
                        {eligibilityResult.adresseApproximative && (
                          <div className="alert alert-warning mb-6">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            L'adresse saisie diffère de celle testée (
                            {eligibilityResult.adresseTestee}
                            ). Affinez la saisie pour un résultat plus précis.
                          </div>
                        )}

                        <div className="table-responsive d-none d-md-block">
                          <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-4">
                            <thead>
                              <tr className="fw-bold text-muted bg-light">
                                <th className="ps-4 min-w-200px rounded-start">
                                  {intl.formatMessage({id: 'DIVOPS.RESULTS.COL.ADRESSE'})}
                                </th>
                                <th className="min-w-80px">Type</th>
                                <th className="min-w-100px">
                                  {intl.formatMessage({id: 'DIVOPS.RESULTS.COL.OUVERT'})}
                                </th>
                                <th className="min-w-150px">
                                  {intl.formatMessage({id: 'DIVOPS.RESULTS.COL.POP'})}
                                </th>
                                <th className="min-w-100px">
                                  {intl.formatMessage({id: 'DIVOPS.RESULTS.COL.ZONE'})}
                                </th>
                                <th className="min-w-100px text-end rounded-end pe-4">
                                  {intl.formatMessage({id: 'DIVOPS.RESULTS.COL.ACTION'})}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Ligne FTTO */}
                              <tr>
                                <td className="ps-4 text-dark fw-bold fs-6">
                                  {eligibilityResult.adresseTestee}
                                </td>
                                <td>
                                  <span className="badge badge-light-info text-uppercase fw-semibold">FTTO</span>
                                </td>
                                <td>
                                  {eligibilityResult.ftto.ouvert === 'Oui' ? (
                                    <span className="badge badge-light-success fs-7 fw-semibold">
                                      <i className="fas fa-check-circle me-1"></i>Oui
                                    </span>
                                  ) : eligibilityResult.ftto.ouvert === 'Prévu' ? (
                                    <span className="badge badge-light-warning fs-7 fw-semibold">
                                      <i className="fas fa-clock me-1"></i>Prévu
                                    </span>
                                  ) : (
                                    <span className="badge badge-light-danger fs-7 fw-semibold">
                                      <i className="fas fa-times-circle me-1"></i>Non
                                    </span>
                                  )}
                                </td>
                                <td className="text-dark fs-6">
                                  {eligibilityResult.ftto.ouvert === 'Oui'
                                    ? `PoP doublé : ${eligibilityResult.ftto.popDoubler}`
                                    : eligibilityResult.ftto.dateOuverture}
                                </td>
                                <td>
                                  <span className="badge badge-light-primary text-uppercase fs-7 fw-semibold">
                                    {eligibilityResult.ftto.zone.toUpperCase()}
                                  </span>
                                </td>
                                <td className="text-end pe-4">
                                  {canCommandFtto && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleSelectLien('ftto')}
                                    >
                                      {intl.formatMessage({id: 'DIVOPS.RESULTS.COMMANDER'})} FTTO
                                    </button>
                                  )}
                                </td>
                              </tr>

                              {/* Ligne FTTE */}
                              {eligibilityResult.ftte && (
                                <tr>
                                  <td className="ps-4 text-dark fw-bold fs-6">
                                    {eligibilityResult.adresseTestee}
                                  </td>
                                  <td>
                                    <span className="badge badge-light-info fw-semibold">FTTE</span>
                                  </td>
                                  <td>
                                    {eligibilityResult.ftte.eligible ? (
                                      <span className="badge badge-light-success fs-7 fw-semibold">
                                        <i className="fas fa-check-circle me-1"></i>
                                        {eligibilityResult.ftte.delai}
                                      </span>
                                    ) : (
                                      <span className="badge badge-light-danger fs-7 fw-semibold">
                                        <i className="fas fa-times-circle me-1"></i>Non éligible
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-dark fs-6">{eligibilityResult.ftte.pm ?? '—'}</td>
                                  <td>
                                    {eligibilityResult.ftte.zone && (
                                      <span className="badge badge-light-primary fs-7 fw-semibold">
                                        {eligibilityResult.ftte.zone.toUpperCase()}
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-end pe-4">
                                    {canCommandFtte && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleSelectLien('ftte')}
                                      >
                                        {intl.formatMessage({id: 'DIVOPS.RESULTS.COMMANDER'})} FTTE
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              )}

                              {!canCommandFtto && !canCommandFtte && (
                                <tr>
                                  <td colSpan={6} className="text-center text-muted py-6">
                                    Cette adresse n'est pas éligible.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="d-md-none d-flex flex-column gap-3">
                          {/* Carte FTTO */}
                          <div className="border rounded p-4">
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                              <span className="fw-bold text-dark fs-6">
                                {eligibilityResult.adresseTestee}
                              </span>
                              <span className="badge badge-light-info fw-semibold flex-shrink-0">
                                FTTO
                              </span>
                            </div>
                            <div className="mb-2">
                              {eligibilityResult.ftto.ouvert === 'Oui' ? (
                                <span className="badge badge-light-success fs-7 fw-semibold">
                                  <i className="fas fa-check-circle me-1"></i>Oui
                                </span>
                              ) : eligibilityResult.ftto.ouvert === 'Prévu' ? (
                                <span className="badge badge-light-warning fs-7 fw-semibold">
                                  <i className="fas fa-clock me-1"></i>Prévu
                                </span>
                              ) : (
                                <span className="badge badge-light-danger fs-7 fw-semibold">
                                  <i className="fas fa-times-circle me-1"></i>Non
                                </span>
                              )}
                              <span className="badge badge-light-primary fs-7 fw-semibold ms-2">
                                {eligibilityResult.ftto.zone.toUpperCase()}
                              </span>
                            </div>
                            <div className="text-muted fs-7 mb-3">
                              {eligibilityResult.ftto.ouvert === 'Oui'
                                ? `PoP doublé : ${eligibilityResult.ftto.popDoubler}`
                                : eligibilityResult.ftto.dateOuverture}
                            </div>
                            {canCommandFtto && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary w-100"
                                onClick={() => handleSelectLien('ftto')}
                              >
                                {intl.formatMessage({id: 'DIVOPS.RESULTS.COMMANDER'})} FTTO
                              </button>
                            )}
                          </div>

                          {/* Carte FTTE */}
                          {eligibilityResult.ftte && (
                            <div className="border rounded p-4">
                              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                <span className="fw-bold text-dark fs-6">
                                  {eligibilityResult.adresseTestee}
                                </span>
                                <span className="badge badge-light-info fw-semibold flex-shrink-0">
                                  FTTE
                                </span>
                              </div>
                              <div className="mb-2">
                                {eligibilityResult.ftte.eligible ? (
                                  <span className="badge badge-light-success fs-7 fw-semibold">
                                    <i className="fas fa-check-circle me-1"></i>
                                    {eligibilityResult.ftte.delai}
                                  </span>
                                ) : (
                                  <span className="badge badge-light-danger fs-7 fw-semibold">
                                    <i className="fas fa-times-circle me-1"></i>Non éligible
                                  </span>
                                )}
                                {eligibilityResult.ftte.zone && (
                                  <span className="badge badge-light-primary fs-7 fw-semibold ms-2">
                                    {eligibilityResult.ftte.zone.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="text-muted fs-7 mb-3">
                                {eligibilityResult.ftte.pm ?? '—'}
                              </div>
                              {canCommandFtte && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary w-100"
                                  onClick={() => handleSelectLien('ftte')}
                                >
                                  {intl.formatMessage({id: 'DIVOPS.RESULTS.COMMANDER'})} FTTE
                                </button>
                              )}
                            </div>
                          )}

                          {!canCommandFtto && !canCommandFtte && (
                            <div className="text-center text-muted py-6">
                              Cette adresse n'est pas éligible.
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Step 3 — Formulaire de commande */}
                {currentStep === 3 && eligibilityResult && (
                  <div>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-8">
                      <h2 className="fw-bold mb-0">
                        {intl.formatMessage({id: 'DIVOPS.ORDER.TITLE'})}
                      </h2>
                      <button
                        type="button"
                        className="btn btn-light-primary"
                        onClick={() => setCurrentStep(2)}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        {intl.formatMessage({id: 'DIVOPS.ORDER.BACK'})}
                      </button>
                    </div>

                    <div className="alert alert-primary d-flex flex-wrap align-items-center gap-2 mb-8">
                      <i className="fas fa-map-marker-alt fs-3 text-primary me-3"></i>
                      <div>
                        <span className="fw-bold">{eligibilityResult.adresseTestee}</span>
                        {' — '}
                        <span className="badge badge-light-info ms-1">
                          {orderForm.typeLienDivop.toUpperCase()}
                        </span>
                        {' — '}
                        <span className="badge badge-light-primary ms-1">
                          {orderForm.typeLienDivop === 'ftto'
                            ? eligibilityResult.ftto.zone.toUpperCase()
                            : eligibilityResult.ftte?.zone?.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <div className="row g-8">
                      {/* Zone Lien souhaité */}
                      <div className="col-xl-6">
                        <div className="card card-bordered h-100">
                          <div className="card-header">
                            <h3 className="card-title fw-bold">
                              <i className="fas fa-network-wired text-primary me-2 fs-4"></i>
                              {intl.formatMessage({id: 'DIVOPS.ORDER.ZONE_LIEN.TITLE'})}
                            </h3>
                          </div>
                          <div className="card-body">
                            <div className="fv-row mb-6">
                              <label className="fw-semibold fs-6 mb-2 required">
                                {intl.formatMessage({id: 'DIVOPS.ORDER.TYPE_LIEN'})}
                              </label>
                              <select
                                id="order-produitId"
                                className={`form-select form-select-solid${invalidClass('produitId')}`}
                                value={orderForm.produitId}
                                onChange={(e) =>
                                  setOrderForm({...orderForm, produitId: e.target.value})
                                }
                              >
                                <option value="">Sélectionnez un produit</option>
                                {activeProduits.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="fv-row mb-6">
                              <label className="fw-semibold fs-6 mb-2 required">
                                {intl.formatMessage({id: 'DIVOPS.ORDER.DUREE_ENGAGEMENT'})}
                              </label>
                              <select
                                id="order-dureeEngagement"
                                className={`form-select form-select-solid${invalidClass('dureeEngagement')}`}
                                value={orderForm.dureeEngagement}
                                onChange={(e) =>
                                  setOrderForm({...orderForm, dureeEngagement: e.target.value})
                                }
                              >
                                {DUREES_ENGAGEMENT.map((d) => (
                                  <option key={d.value} value={d.value}>
                                    {d.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="fv-row mb-6">
                              <div className="form-check form-check-custom form-check-solid">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="gtr247"
                                  checked={orderForm.gtr247}
                                  onChange={(e) =>
                                    setOrderForm({...orderForm, gtr247: e.target.checked})
                                  }
                                />
                                <label
                                  className="form-check-label fw-semibold fs-6"
                                  htmlFor="gtr247"
                                >
                                  {intl.formatMessage({id: 'DIVOPS.ORDER.GTR247'})}
                                </label>
                              </div>
                            </div>

                            <div className="fv-row mb-6">
                              <label className="fw-semibold fs-6 mb-2">
                                {intl.formatMessage({id: 'DIVOPS.ORDER.REF_COMMANDE'})}
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-solid"
                                maxLength={REFERENCE_MAX_LENGTH}
                                value={orderForm.referenceCommandeInterne}
                                onChange={(e) =>
                                  setOrderForm({
                                    ...orderForm,
                                    referenceCommandeInterne: e.target.value,
                                  })
                                }
                              />
                              <div
                                className={`form-text ${
                                  orderForm.referenceCommandeInterne.length >= REFERENCE_MAX_LENGTH
                                    ? 'text-warning'
                                    : 'text-muted'
                                }`}
                              >
                                {intl.formatMessage({id: 'DIVOPS.ORDER.REF_MAX_LENGTH'})}
                              </div>
                            </div>

                            <div className="fv-row mb-6">
                              <label className="fw-semibold fs-6 mb-2">
                                {intl.formatMessage({id: 'DIVOPS.ORDER.REF_FACTURATION'})}
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-solid"
                                maxLength={REFERENCE_MAX_LENGTH}
                                value={orderForm.referenceFacturationInterne}
                                onChange={(e) =>
                                  setOrderForm({
                                    ...orderForm,
                                    referenceFacturationInterne: e.target.value,
                                  })
                                }
                              />
                              <div
                                className={`form-text ${
                                  orderForm.referenceFacturationInterne.length >=
                                  REFERENCE_MAX_LENGTH
                                    ? 'text-warning'
                                    : 'text-muted'
                                }`}
                              >
                                {intl.formatMessage({id: 'DIVOPS.ORDER.REF_MAX_LENGTH'})}
                              </div>
                            </div>

                            <div className="fv-row mb-0">
                              <label className="fw-semibold fs-6 mb-2">
                                {intl.formatMessage({id: 'DIVOPS.ORDER.COMMENTAIRE'})}
                              </label>
                              <textarea
                                className="form-control form-control-solid"
                                rows={4}
                                value={orderForm.commentaire}
                                onChange={(e) =>
                                  setOrderForm({...orderForm, commentaire: e.target.value})
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Zone Porte de livraison VLAN */}
                      <div className="col-xl-6">
                        <div className="card card-bordered h-100">
                          <div className="card-header">
                            <h3 className="card-title fw-bold">
                              <i className="fas fa-ethernet text-primary me-2 fs-4"></i>
                              {intl.formatMessage({id: 'DIVOPS.ORDER.ZONE_VLAN.TITLE'})}
                            </h3>
                          </div>
                          <div className="card-body">
                            <div className="fv-row mb-6">
                              <label className="fw-semibold fs-6 mb-2 required">
                                {intl.formatMessage({id: 'DIVOPS.ORDER.PORT_LIVRAISON'})}
                              </label>
                              <select
                                id="order-portLivraisonPrincipal"
                                className={`form-select form-select-solid${invalidClass('portLivraisonPrincipal')}`}
                                value={orderForm.portLivraisonPrincipal}
                                disabled={isLoadingPortesCollecte}
                                onChange={(e) =>
                                  setOrderForm({
                                    ...orderForm,
                                    portLivraisonPrincipal: e.target.value,
                                  })
                                }
                              >
                                <option value="">
                                  {isLoadingPortesCollecte
                                    ? 'Chargement...'
                                    : 'Sélectionnez un port de livraison'}
                                </option>
                                {portesCollecte.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="fv-row mb-6">
                              <label className="fw-semibold fs-6 mb-2 required">
                                {intl.formatMessage({id: 'DIVOPS.ORDER.VLAN_LIVRAISON'})}
                              </label>
                              <input
                                type="text"
                                id="order-vlanLivraison"
                                className={`form-control form-control-solid${invalidClass('vlanLivraison')}`}
                                value={orderForm.vlanLivraison}
                                onChange={(e) =>
                                  setOrderForm({...orderForm, vlanLivraison: e.target.value})
                                }
                              />
                            </div>

                            <div className="fv-row mb-6">
                              <div className="form-check form-check-custom form-check-solid">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="optionSecurisation"
                                  checked={orderForm.optionSecurisation}
                                  onChange={(e) =>
                                    setOrderForm({
                                      ...orderForm,
                                      optionSecurisation: e.target.checked,
                                      portLivraisonSecondaire: '',
                                      vlanLivraisonSecondaire: '',
                                    })
                                  }
                                />
                                <label
                                  className="form-check-label fw-semibold fs-6"
                                  htmlFor="optionSecurisation"
                                >
                                  {intl.formatMessage({
                                    id: 'DIVOPS.ORDER.OPTION_SECURISATION.ENABLE',
                                  })}
                                </label>
                              </div>
                            </div>

                            {orderForm.optionSecurisation && (
                              <>
                                <div className="fv-row mb-6">
                                  <label className="fw-semibold fs-6 mb-2">
                                    Port de livraison secondaire
                                  </label>
                                  <select
                                    className="form-select form-select-solid"
                                    value={orderForm.portLivraisonSecondaire}
                                    disabled={isLoadingPortesCollecte}
                                    onChange={(e) =>
                                      setOrderForm({
                                        ...orderForm,
                                        portLivraisonSecondaire: e.target.value,
                                      })
                                    }
                                  >
                                    <option value="">
                                      {isLoadingPortesCollecte
                                        ? 'Chargement...'
                                        : 'Sélectionnez un port de livraison'}
                                    </option>
                                    {portesCollecte.map((p) => (
                                      <option key={p.id} value={p.id}>
                                        {p.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="fv-row mb-0">
                                  <label className="fw-semibold fs-6 mb-2">
                                    {intl.formatMessage({id: 'DIVOPS.ORDER.VLAN_SECONDAIRE'})}
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-solid"
                                    value={orderForm.vlanLivraisonSecondaire}
                                    onChange={(e) =>
                                      setOrderForm({
                                        ...orderForm,
                                        vlanLivraisonSecondaire: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Zone Information client */}
                    <div className="row g-8 mt-0">
                      <div className="col-12">
                        <div className="card card-bordered">
                          <div className="card-header">
                            <h3 className="card-title fw-bold">
                              <i className="fas fa-user text-primary me-2 fs-4"></i>
                              {intl.formatMessage({id: 'DIVOPS.CLIENT.ZONE.TITLE'})}
                            </h3>
                          </div>
                          <div className="card-body">
                            <div className="fv-row mb-6">
                              <div className="d-flex align-items-center gap-6 flex-wrap">
                                <div className="form-check form-check-custom form-check-solid">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="contactExistant"
                                    checked={clientForm.contactExistant}
                                    onChange={(e) =>
                                      setClientForm({
                                        ...clientForm,
                                        contactExistant: e.target.checked,
                                        contactId: '',
                                        emailContact: '',
                                      })
                                    }
                                  />
                                  <label
                                    className="form-check-label fw-semibold fs-6"
                                    htmlFor="contactExistant"
                                  >
                                    {intl.formatMessage({id: 'DIVOPS.CLIENT.CONTACT_EXISTANT'})}
                                  </label>
                                </div>
                                {clientForm.contactExistant && (
                                  <select
                                    id="order-contactId"
                                    className={`form-select form-select-solid w-300px${invalidClass('contactId')}`}
                                    value={clientForm.contactId}
                                    disabled={isLoadingContacts}
                                    onChange={(e) => {
                                      const email = e.target.value;
                                      const contact = contacts.find((c) => c.email === email);
                                      setClientForm({
                                        ...clientForm,
                                        contactId: email,
                                        nom: contact?.nom ?? clientForm.nom,
                                        prenom: contact?.prenom ?? clientForm.prenom,
                                      });
                                    }}
                                  >
                                    <option value="">
                                      {isLoadingContacts
                                        ? 'Chargement...'
                                        : 'Sélectionnez un contact'}
                                    </option>
                                    {contacts.map((c) => (
                                      <option key={c.email} value={c.email}>
                                        {c.prenom} {c.nom} ({c.email})
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </div>

                            {!clientForm.contactExistant && (
                              <div className="fv-row mb-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.EMAIL'})}
                                </label>
                                <input
                                  type="email"
                                  id="order-emailContact"
                                  className={`form-control form-control-solid${invalidClass('emailContact')}`}
                                  value={clientForm.emailContact}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, emailContact: e.target.value})
                                  }
                                />
                              </div>
                            )}

                            <div className="row g-6 mb-6">
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.NOM'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-nom"
                                  className={`form-control form-control-solid${invalidClass('nom')}`}
                                  value={clientForm.nom}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, nom: e.target.value})
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.PRENOM'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-prenom"
                                  className={`form-control form-control-solid${invalidClass('prenom')}`}
                                  value={clientForm.prenom}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, prenom: e.target.value})
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.TEL_FIXE'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-telephoneFixe"
                                  className={`form-control form-control-solid${invalidClass('telephoneFixe')}`}
                                  value={clientForm.telephoneFixe}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, telephoneFixe: e.target.value})
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.TEL_MOBILE'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-telephoneMobile"
                                  className={`form-control form-control-solid${invalidClass('telephoneMobile')}`}
                                  value={clientForm.telephoneMobile}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, telephoneMobile: e.target.value})
                                  }
                                />
                              </div>
                            </div>

                            <div className="separator separator-dashed mb-6"></div>
                            <div className="row g-6">
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.SIRET'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-siret"
                                  className={`form-control form-control-solid${invalidClass('siret')}`}
                                  value={clientForm.siret}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, siret: e.target.value})
                                  }
                                />
                                <SiretAdresseSelect
                                  siret={clientForm.siret}
                                  onSelect={(suggestion, societe) =>
                                    setClientForm({
                                      ...clientForm,
                                      societe: societe ?? clientForm.societe,
                                      adresse: [suggestion.numero, suggestion.rue]
                                        .filter(Boolean)
                                        .join(' '),
                                      codePostal: suggestion.codePostal,
                                      ville: suggestion.ville,
                                    })
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.SOCIETE'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-societe"
                                  className={`form-control form-control-solid${invalidClass('societe')}`}
                                  value={clientForm.societe}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, societe: e.target.value})
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.ADRESSE'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-adresse"
                                  className={`form-control form-control-solid${invalidClass('adresse')}`}
                                  value={clientForm.adresse}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, adresse: e.target.value})
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.DETAILS'})}
                                </label>
                                <input
                                  type="text"
                                  className="form-control form-control-solid"
                                  value={clientForm.details}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, details: e.target.value})
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.CODE_POSTAL'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-codePostal"
                                  className={`form-control form-control-solid${invalidClass('codePostal')}`}
                                  value={clientForm.codePostal}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, codePostal: e.target.value})
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="fw-semibold fs-6 mb-2 required">
                                  {intl.formatMessage({id: 'DIVOPS.CLIENT.VILLE'})}
                                </label>
                                <input
                                  type="text"
                                  id="order-ville"
                                  className={`form-control form-control-solid${invalidClass('ville')}`}
                                  value={clientForm.ville}
                                  onChange={(e) =>
                                    setClientForm({...clientForm, ville: e.target.value})
                                  }
                                />
                              </div>
                            </div>

                            {/* FTTE — champs adresse spécifiques */}
                            {orderForm.typeLienDivop === 'ftte' && (
                              <>
                                <div className="separator separator-dashed my-6"></div>
                                <div className="row g-6">
                                  <div className="col-md-4">
                                    <label className="fw-semibold fs-6 mb-2">Porte</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-solid"
                                      value={clientForm.porte}
                                      onChange={(e) =>
                                        setClientForm({...clientForm, porte: e.target.value})
                                      }
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label className="fw-semibold fs-6 mb-2">Escalier</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-solid"
                                      value={clientForm.escalier}
                                      onChange={(e) =>
                                        setClientForm({...clientForm, escalier: e.target.value})
                                      }
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label className="fw-semibold fs-6 mb-2">Bâtiment</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-solid"
                                      value={clientForm.batiment}
                                      onChange={(e) =>
                                        setClientForm({...clientForm, batiment: e.target.value})
                                      }
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label className="fw-semibold fs-6 mb-2">Ensemble</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-solid"
                                      value={clientForm.ensemble}
                                      onChange={(e) =>
                                        setClientForm({...clientForm, ensemble: e.target.value})
                                      }
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label className="fw-semibold fs-6 mb-2">PTO</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-solid"
                                      value={clientForm.pto}
                                      onChange={(e) =>
                                        setClientForm({...clientForm, pto: e.target.value})
                                      }
                                    />
                                  </div>
                                </div>
                              </>
                            )}

                            <div className="separator separator-dashed my-6"></div>
                            <div className="form-check form-check-custom form-check-solid">
                              <input
                                className={`form-check-input${invalidClass('conformiteRgpd')}`}
                                type="checkbox"
                                id="order-conformiteRgpd"
                                checked={clientForm.conformiteRgpd}
                                onChange={(e) =>
                                  setClientForm({...clientForm, conformiteRgpd: e.target.checked})
                                }
                              />
                              <label
                                className="form-check-label fw-semibold fs-6 required"
                                htmlFor="order-conformiteRgpd"
                              >
                                {intl.formatMessage({id: 'DIVOPS.CLIENT.CONFORMITE.RGPD'})}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-8">
                      <button
                        type="button"
                        className="btn btn-lg btn-primary"
                        onClick={handleSubmitOrder}
                        disabled={createCommandeMutation.isLoading}
                      >
                        {createCommandeMutation.isLoading ? (
                          <span className="indicator-progress d-flex align-items-center">
                            {intl.formatMessage({id: 'WAIT'})}
                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                          </span>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane me-2"></i>
                            {intl.formatMessage({id: 'DIVOPS.ORDER.SUBMIT'})}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* end::Content */}
            </div>
          </div>
        </div>
      </div>

      {currentStep === 1 && (
        <div className="card mx-5 mt-5">
          <div className="card-header border-0 pt-6">
            <div className="card-title">
              <Level3Title title={intl.formatMessage({id: 'DIVOPS.HISTORY.RECENT.TITLE'})} />
            </div>
            <div className="card-toolbar">
              {/* Éligibilité en masse masquée temporairement
              <Link
                to="/divops/eligibilite-masse"
                className="btn btn-sm btn-light-primary me-2"
              >
                {intl.formatMessage({id: 'DIVOPS.MASSE.LINK'})}
              </Link>
              */}
              <Link to="/divops/historique" className="btn btn-sm btn-light-primary">
                {intl.formatMessage({id: 'DIVOPS.HISTORY.LINK'})}
              </Link>
            </div>
          </div>
          <div className="card-body">
            {recentSearches.length > 0 ? (
              <>
                <div className="table-responsive d-none d-md-block">
                  <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-4">
                    <thead>
                      <tr className="fw-bold text-muted bg-light">
                        <th className="ps-4 min-w-200px rounded-start">
                          {intl.formatMessage({id: 'DIVOPS.HISTORY.COL.ADRESSE'})}
                        </th>
                        <th className="min-w-100px">
                          {intl.formatMessage({id: 'DIVOPS.HISTORY.COL.CODE_POSTAL'})}
                        </th>
                        <th className="min-w-150px">
                          {intl.formatMessage({id: 'DIVOPS.HISTORY.COL.COMMUNE'})}
                        </th>
                        <th className="min-w-150px">
                          {intl.formatMessage({id: 'DIVOPS.HISTORY.COL.DATE'})}
                        </th>
                        <th className="min-w-150px">
                          {intl.formatMessage({id: 'DIVOPS.HISTORY.COL.EMAIL'})}
                        </th>
                        <th className="min-w-100px">
                          {intl.formatMessage({id: 'DIVOPS.HISTORY.COL.ACCOUNT_ID'})}
                        </th>
                        <th className="min-w-60px text-end pe-4 rounded-end"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSearches.map((search) => (
                        <tr key={search.id}>
                          <td className="ps-4 text-dark fs-6">{search.adresse}</td>
                          <td className="text-dark fs-6">{search.codePostal}</td>
                          <td className="text-dark fs-6">{search.commune}</td>
                          <td className="text-dark fs-6">
                            {DateTime.fromISO(search.createdAt).toFormat('dd/MM/yyyy HH:mm')}
                          </td>
                          <td className="text-dark fs-6">{search.userEmail}</td>
                          <td className="text-dark fs-6">{search.accountId}</td>
                          <td className="text-end pe-4">
                            <button
                              type="button"
                              className="btn btn-sm btn-icon btn-light-primary"
                              title={intl.formatMessage({id: 'DIVOPS.HISTORY.RELAUNCH'})}
                              disabled={!search.codeInsee}
                              onClick={() =>
                                handleRelaunchSearch(
                                  {
                                    adresse: search.adresse,
                                    codePostal: search.codePostal,
                                    codeInsee: search.codeInsee,
                                  },
                                  `${search.adresse} ${search.codePostal} ${search.commune}`
                                )
                              }
                            >
                              <i className="fas fa-search"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-md-none d-flex flex-column gap-3">
                  {recentSearches.map((search) => (
                    <div key={search.id} className="border rounded p-4">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <span className="fw-bold text-dark fs-6">{search.adresse}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-icon btn-light-primary flex-shrink-0"
                          title={intl.formatMessage({id: 'DIVOPS.HISTORY.RELAUNCH'})}
                          disabled={!search.codeInsee}
                          onClick={() =>
                            handleRelaunchSearch(
                              {
                                adresse: search.adresse,
                                codePostal: search.codePostal,
                                codeInsee: search.codeInsee,
                              },
                              `${search.adresse} ${search.codePostal} ${search.commune}`
                            )
                          }
                        >
                          <i className="fas fa-search"></i>
                        </button>
                      </div>
                      <div className="text-muted fs-7">
                        {search.codePostal} {search.commune}
                      </div>
                      <div className="text-muted fs-7">
                        {DateTime.fromISO(search.createdAt).toFormat('dd/MM/yyyy HH:mm')}
                      </div>
                      <div className="text-muted fs-7">{search.userEmail}</div>
                      <div className="text-muted fs-7">ID compte : {search.accountId}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-muted">{intl.formatMessage({id: 'DIVOPS.HISTORY.EMPTY'})}</div>
            )}
          </div>
        </div>
      )}

      <DivopsAssistanceModal
        show={showAssistanceModal}
        hide={() => setShowAssistanceModal(false)}
      />
    </Container>
  );
};

export default DivopsPage;
